import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuthStore } from '@/store/authStore';
import { subscriptionService } from '@/services/subscriptionService';
import { Check, Sparkles, Zap, Shield, Clock, Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string;
}

export function PaywallModal({ isOpen, onClose, feature }: PaywallModalProps) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const { startCheckout, isLoading, error, clearError } = useSubscription();
  const { isAuthenticated, setShowAuthModal, setAuthModalMode } = useAuthStore();
  const plans = subscriptionService.getPricingPlans();

  const handleSubscribe = async () => {
    clearError();

    // If not authenticated, show auth modal instead
    if (!isAuthenticated) {
      onClose();
      setAuthModalMode('signin');
      setShowAuthModal(true);
      return;
    }

    await startCheckout(billingCycle);
  };

  const features = [
    { icon: Sparkles, text: 'Unlimited aspirations, goals & lists' },
    { icon: Zap, text: 'AI-powered insights & suggestions' },
    { icon: Shield, text: 'Cloud sync & backup' },
    { icon: Clock, text: 'Schedule optimization' },
  ];

  const selectedPlan = billingCycle === 'monthly' ? plans.monthly : plans.yearly;
  const monthlyEquivalent = billingCycle === 'yearly'
    ? (plans.yearly.price / 12).toFixed(2)
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upgrade to GodMode Pro"
      size="md"
      scrollBehavior="outside"
    >
      <div className="space-y-6">
        {feature && (
          <p className="text-muted-foreground text-center">
            <span className="font-medium text-foreground">{feature}</span> requires a Pro subscription.
          </p>
        )}

        {/* Pricing Toggle */}
        <div className="flex justify-center">
          <div className="inline-flex rounded-lg border bg-muted p-1">
            <button
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                billingCycle === 'monthly'
                  ? 'bg-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                billingCycle === 'yearly'
                  ? 'bg-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly
              <span className="ml-1 text-xs text-green-600 font-semibold">Save £7.89</span>
            </button>
          </div>
        </div>

        {/* Price Display */}
        <Card className="border-primary">
          <CardContent className="p-6 text-center">
            <div className="text-4xl font-bold">
              £{selectedPlan.price.toFixed(2)}
            </div>
            <div className="text-muted-foreground">
              per {billingCycle === 'monthly' ? 'month' : 'year'}
            </div>
            {monthlyEquivalent && (
              <div className="text-sm text-green-600 mt-1">
                That's just £{monthlyEquivalent}/month!
              </div>
            )}
            <div className="text-sm text-muted-foreground mt-2">
              14-day free trial included
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <div className="space-y-3">
          {features.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-3">
              <div className="rounded-full bg-primary/10 p-1.5">
                <Icon className="h-4 w-4 text-primary" />
              </div>
              <span className="text-sm">{text}</span>
            </div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm text-center">
            {error}
          </div>
        )}

        {/* CTA */}
        <div className="space-y-3">
          <Button
            className="w-full"
            onClick={handleSubscribe}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Redirecting to checkout...
              </>
            ) : (
              'Start 14-Day Free Trial'
            )}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Cancel anytime. No questions asked.
          </p>
        </div>

        {/* Trust indicators */}
        <div className="flex items-center justify-center gap-4 pt-2 border-t">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Check className="h-3 w-3" />
            <span>Secure checkout</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Check className="h-3 w-3" />
            <span>Instant access</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}
